import {
  all,
  call,
  put,
  select,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';
import i18next from 'i18next';

import { get, getData, post, SUBSCRIPTION_KEY_HEADER } from '../services/api';
import {
  allPetsGrouped,
  cancelMultibrandPolicy as CANCEL_MULTIBRAND_POLICY,
  cancelMultibrandPolicyError,
  cancelMultibrandPolicySuccess,
  changeCoverageMultimodalFailed,
  changeCoverageMultimodalLoaded,
  changeCoveragePreviewLoaded,
  getChangeCoveragePreviewError,
  getCoveragePeriodFailure,
  getCoveragePeriodRequest as GET_COVERAGE_PERIOD_REQUEST,
  getCoveragePeriodSuccess,
  getCoverageQuoteProductSuccess,
  getHasCareCreditUrlFailed,
  getHasCareCreditUrlSuccess,
  getMultibrandDefaultPolicy as GET_MULTIBRAND_DEFAULT_POLICY,
  getMultibrandDefaultPolicyError,
  getMultibrandDefaultPolicyLoaded,
  getMultibrandPolicies as GET_MULTIBRAND_POLICIES,
  getMultibrandPoliciesError,
  getMultibrandPoliciesLoaded,
  getOrcaCoveragePreview as GET_ORCA_COVERAGE_PREVIEW,
  getOrcaCoveragePreviewFailed,
  getOrcaCoveragePreviewSuccess,
  getOrcaPowerUp as GET_ORCA_POWER_UP,
  getOrcaPowerUpsBatch as GET_ORCA_POWER_UPS_BATCH,
  getPolicyDetail as GET_POLICY_DETAIL,
  getPolicyDetailError,
  getPolicyDetailLoaded,
  getProxyPolicy as GET_PROXY_POLICY,
  loadMultiPolicies,
  loadPlanSummarySeeMoreInfoAttempt,
  loadPoliciesCardInformation,
  loadPoliciesPaymentNumber,
  loadWhatWeCoverInfoAttempt,
  multiModalDataLoaded,
  petsGrouped,
  planSummarySeeMoreInfoLoaded,
  policiesOverviewLoaded,
  getProxyPolicyFailed,
  getProxyPolicySuccess,
  policyDetailsBenefitsLoaded,
  policyNotificationsLoaded,
  quotePowerUpChanged,
  rateCoverageError,
  rateCoverageSuccess,
  rateQuoteUpdateAttempt,
  rateQuoteUpdateLoaded,
  reimbursementMethodLoaded,
  requestCancelRenewalFailed,
  requestCancelRenewalSuccess,
  saveOrcaQuote as SAVE_ORCA_QUOTE,
  saveOrcaQuoteFailed,
  saveOrcaQuoteSuccess,
  storeAllPolicies,
  storePolicies,
  submitChangeCoverageFailed,
  submitChangeCoverageSuccess,
  submitCoveragePeriod as SUBMIT_COVERAGE_PERIOD,
  submitCoveragePeriodFailed,
  submitCoveragePeriodSuccess,
  validateBreedMultibrand as VALIDATE_BREED_MULTIBRAND,
  validateChangeCoverageFailed,
  validateChangeCoverageSuccess,
  waitingPeriodsLoaded,
  whatWeCoverInfoLoaded,
} from '../actions';
import {
  getOrcaPowerUp,
  getOrcaPowerUpsBatch,
  validateBreedMultibrand,
} from './orcaInsurance';
import { POLICY_STATUS } from '../constants';
import { MARKETING_CHANNEL_ID } from '../theme/theme.constants';
import { doFetch, doNothing, getOrcaStateByPostalCode, serializeForUri, sleep } from '../services/utils';
import {
  usePetCloudClaimV2217178ForSagas,
  usePetCloudPolicyInfoAndPet,
  usePetCloudPolicyV1T189581,
  usePolicyApiV1ForSagas,
  usePolicyApiV1T153269ForSagas,
  usePolicyApiV1T153274ForSagas,
  usePolicyApiV1T153276ForSagas,
  usePolicyApiV1T159611ForSagas,
  usePolicySummaryV1ForSagas,
} from '../services/featureFlagsForSagas';
import { WEBVIEW_API_TOKEN } from '../services/constants';
import { getOrcaPolicyCodeByName } from '../components/insurance/changeCoverage/utils/orcaChangeCoverage.utils';

function getPetPolicies({ policies, petId }) {
  return policies.filter((pet) => (
    pet.PetInformation.PetId === petId
  )).map((pet) => ({ ...pet.Policy, ...pet.BillingInformation }));
}

function* groupPets(policiesData, allPets = false) {
  const pets = [];

  policiesData.forEach(({ PetInformation }) => {
    const isPetGrouped =
      pets.find((pet) => pet.PetId === PetInformation.PetId);

    if (!isPetGrouped) {
      const pet = {
        ...PetInformation,
        policies: getPetPolicies({
          petId: PetInformation.PetId,
          policies: policiesData,
        }),
      };

      pets.push(pet);
    }
  });

  const action = allPets ? allPetsGrouped : petsGrouped;

  yield put(action(pets));
}

export function* getPolicySummary(dispatch) {
  const usePolicySummaryV1 = yield usePolicySummaryV1ForSagas();

  let apiUrl = 'api';

  if (usePolicySummaryV1) {
    apiUrl = 'policy';
  }

  const url = `${apiUrl}/PMS/Policy/Summary`;
  const response = yield call(get, dispatch, url);

  if (response.success && response.IsValid) {
    const validPolicies = response.Data.filter((policyData) => (
      policyData.Policy.Number
    ));
    const policiesToUpdate = validPolicies.filter((policyData) => (
      policyData.Policy.Status === POLICY_STATUS.active
      || policyData.Policy.Status === POLICY_STATUS.future
    ));

    yield put(storeAllPolicies(response.Data));
    yield put(storePolicies(validPolicies));
    yield put(loadMultiPolicies(policiesToUpdate));
    yield put(loadPoliciesPaymentNumber());
    yield call(groupPets, validPolicies);
    yield call(groupPets, response.Data, true);
  }
}

export function* getPoliciesPaymentNumber(dispatch) {
  const usePolicy = yield usePetCloudPolicyInfoAndPet();
  const usrService = usePolicy ? 'policy' : 'api';
  const response = yield call(
    get,
    dispatch,
    `${usrService}/PMS/PolicyInfoAndPet`,
  );

  if (response.success && response.IsValid) {
    yield put(loadPoliciesCardInformation(response.Data));
  }
}

function* getReimbursementMethodLegacy(dispatch, payload) {
  const response = yield call(
    get,
    dispatch,
    'api/PMS/Claim/GetReimbursementMethod',
    { policyNumber: payload },
  );

  if (response.success) {
    yield put(reimbursementMethodLoaded({ eftData: response.Data }));
  }
}

function* getReimbursementMethod(dispatch, payload) {
  const response = yield call(
    get,
    dispatch,
    'claims/claims/GetReimbursementMethod',
    { policyNumber: payload },
  );

  if (response.success && response.IsValid) {
    const { CareCredit, EftInformation } = response.Data;

    yield put(reimbursementMethodLoaded({
      careCreditData: CareCredit,
      eftData: EftInformation,
    }));
  }
}

export function* loadReimbursementMethod(dispatch, { payload }) {
  const useGetReimbursementV2 = yield usePetCloudClaimV2217178ForSagas();

  if (useGetReimbursementV2) {
    yield call(getReimbursementMethod, dispatch, payload);
  } else {
    yield call(getReimbursementMethodLegacy, dispatch, payload);
  }
}

export function* loadPolicyBenefits(dispatch, { payload }) {
  const usePolicyApiV1 = yield usePolicyApiV1ForSagas();
  const urlService = usePolicyApiV1 ? 'policy' : 'api';

  const response = yield call(
    get,
    dispatch,
    `${urlService}/PMS/Policy/Details/${encodeURI(payload)}`,
  );

  if (response.success) {
    yield put(policyDetailsBenefitsLoaded(response.Data));
  }
}

export function* loadMultiModalData(dispatch, { payload }) {
  const { CustomerId } = payload;

  const usePolicyApiV1 = yield usePolicyApiV1ForSagas();

  let urlService = 'api';

  if (usePolicyApiV1) {
    urlService = 'policy';
  }

  const url = `${urlService}/PMS/Policy/GetOptionalBenefits/${CustomerId}`;
  const response = yield call(get, dispatch, url);

  if (response.success && response.IsValid) {
    yield put(multiModalDataLoaded(response.Data));
  }
}

/**
 * Only for Figo pet/policies, according to Defect 163335
 */
export function* loadPoliciesOverview(dispatch) {
  const usePolicyApiV1T153276 = yield usePolicyApiV1T153276ForSagas();

  const urlService = usePolicyApiV1T153276 ? 'policy' : 'api';
  const url = `${urlService}/PMS/Policy/GetPolicyCoverageBalances`;

  const response = yield call(get, dispatch, url);

  if (response.success) {
    yield put(policiesOverviewLoaded(response.Data));
  }
}

export function* loadPolicyNotifications(dispatch, { payload }) {
  const usePolicyApiV1T153274 = yield usePolicyApiV1T153274ForSagas();
  const urlService = usePolicyApiV1T153274 ? 'policy' : 'api';
  const url = `${urlService}/PMS/Policy/Notifications/${encodeURI(payload)}`;
  const response = yield call(get, dispatch, url);

  if (response.success) {
    yield put(policyNotificationsLoaded(response.Data));
  }
}

export function* loadWhatWeCoverInfo(dispatch, { payload }) {
  const { effectiveDate, policyNumber, stateAbbr } = payload;
  const usePetCloudCustomer213990 = yield usePolicyApiV1T153274ForSagas();
  const urlService = usePetCloudCustomer213990 ? 'customer' : 'api';

  yield put(loadWhatWeCoverInfoAttempt());

  const url = `${urlService}/InsuranceProduct/GetWhatWeCoverInfo/`
    + `${stateAbbr}/${effectiveDate}/${policyNumber}`;

  const response = yield call(get, dispatch, url);

  if (response.success && response.IsValid) {
    yield put(whatWeCoverInfoLoaded(response.Data));
  }
}

export function* loadPlanSummarySeeMoreInfo(dispatch, { payload }) {
  const { effectiveDate, stateAbbr, policyNumber } = payload;

  yield put(loadPlanSummarySeeMoreInfoAttempt());

  const url = 'api/Policy/GetInsuranceProductCoverageXPlan';

  const response = yield call(get, dispatch,
    `${url}/${stateAbbr}/${effectiveDate}/${policyNumber}`);

  if (response.success && response.IsValid) {
    yield put(planSummarySeeMoreInfoLoaded(response.Data));
  }
}

export function* getWaitingPeriods(dispatch, { payload }) {
  const { policyNumber } = payload;

  const response = yield call(
    get,
    dispatch,
    `api/PMS/Policy/WaitingPeriods/${policyNumber}`,
  );

  if (response.IsValid) {
    yield put(waitingPeriodsLoaded(response.Data));
  }
}

export function* getChangeCoveragePreview(dispatch, { payload }) {
  const usePolicyApiV1T153269 = yield usePolicyApiV1T153269ForSagas();

  const urlService = usePolicyApiV1T153269 ? 'policy' : 'api';

  const url = `${urlService}/PMS/Policy/ChangeCoverageInformation`;

  const response = yield call(
    get,
    dispatch,
    `${url}/${payload.policyNumber}`,
  );

  if (response.success && response.IsValid) {
    yield put(changeCoveragePreviewLoaded(response.Data));
  } else {
    yield put(getChangeCoveragePreviewError());
  }
}

export function* rateCoverage(dispatch, { payload }) {
  try {
    const usePolicyApiV1T159611 = yield usePolicyApiV1T159611ForSagas();

    const urlService = usePolicyApiV1T159611 ? 'policy' : 'api';

    const { policyNumber, modifiers } = payload;

    const url = `${urlService}/Policy/CoverageChangeRate/${policyNumber}`;

    const response = yield call(
      post,
      dispatch,
      url,
      { modifiers },
    );

    if (response.success && response.IsValid) {
      yield put(rateCoverageSuccess(response.Data));
    } else {
      yield put(rateCoverageError({
        response,
        success: response.success,
        url,
      }));
    }
  } catch (error) {
    yield put(rateCoverageError({
      message: error.message,
    }));
  }
}

export function* validateChangeCoverage(dispatch, { payload }) {
  const usePolicyApiV1T153269 = yield usePolicyApiV1T153269ForSagas();
  const urlService = usePolicyApiV1T153269 ? 'policy' : 'api';
  const { policyNumber } = payload;

  const response = yield call(
    get,
    dispatch,
    `${urlService}/PMS/Policy/ValidateChangeCoverage/${policyNumber}`,
  );

  if (response.IsValid && response.Data) {
    yield put(validateChangeCoverageSuccess({
      isValid: response.Data.Valid,
    }));
  } else {
    yield put(validateChangeCoverageFailed({
      isValid: !!response.Data?.Valid,
      message: response.Message,
    }));
  }
}

export function* getChangeCoverageMultimodal(dispatch, { payload }) {
  const { policyNumber } = payload;
  const url = `api/InsuranceMultiModal/GetAllByPolicyNumber/${policyNumber}`;
  const response = yield call(get, dispatch, url);

  if (response.success && response.IsValid) {
    yield put(changeCoverageMultimodalLoaded(response.Data));
  } else {
    yield put(changeCoverageMultimodalFailed());
  }
}

export function* updatePowerUp(dispatch, { payload }) {
  const { id, isSelected, modifiers, policyNumber } = payload;

  try {
    yield put(quotePowerUpChanged({ id, isSelected }));
    yield put(rateQuoteUpdateAttempt({ id, isSelected }));

    yield call(rateCoverage, dispatch, {
      payload: { modifiers, policyNumber },
    });
    sleep(800);
    yield put(rateQuoteUpdateLoaded({ id, isSelected }));
  } catch (_) {
    yield put(rateQuoteUpdateLoaded({ id, isSelected: !isSelected }));
  }
}

export function* submitChangeCoverage(dispatch, { payload }) {
  const { body, policyNumber } = payload;

  const usePolicyV1T189581 = yield usePetCloudPolicyV1T189581();

  const urlService = usePolicyV1T189581 ? 'policy' : 'api';
  const url = `${urlService}/Policy/CoverageChange/${policyNumber}`;

  const response = yield call(
    post,
    dispatch,
    url,
    body,
  );

  if (response.success && response.IsValid) {
    yield put(submitChangeCoverageSuccess());
  } else {
    yield put(submitChangeCoverageFailed());
  }
}

export function* getMultibrandPolicies(dispatch) {
  const url = 'policy/MultiBrand/Customer/PetPolicies';

  try {
    const response = yield call(get, dispatch, url);
    if (response.success && response.IsValid) {
      yield put(getMultibrandPoliciesLoaded(response.Data));
    } else {
      yield put(getMultibrandPoliciesError());
    }
  } catch (_) {
    yield put(getMultibrandPoliciesError());
  }
}

export function* getMultibrandDefaultPolicy(dispatch, { payload }) {
  const petId = payload;
  const url = `policy/MultiBrand/Customer/DefaultPolicy/${petId}`;
  try {
    const response = yield call(get, dispatch, url);

    if (response.success && response.IsValid && response.Data) {
      yield put(getMultibrandDefaultPolicyLoaded(response.Data));
    } else {
      yield put(getMultibrandDefaultPolicyError());
    }
  } catch (e) {
    yield put(getMultibrandDefaultPolicyError());
  }
}

export function* getPolicyDetail(dispatch, { payload }) {
  const { marketingChannelId, policyId } = payload;
  // Figo policies are not served by the migrated MultiBrand endpoint yet
  const url = marketingChannelId === MARKETING_CHANNEL_ID.figo
    ? `api/MultiBrand/Customer/PolicyDetail/${policyId}`
    : `policy/MultiBrand/Customer/PolicyDetails/${policyId}`;
  try {
    const response = yield call(get, dispatch, url);

    if (response.success && response.IsValid) {
      yield put(getPolicyDetailLoaded(response.Data));
    } else {
      yield put(getPolicyDetailError());
    }
  } catch (e) {
    yield put(getPolicyDetailError());
  }
}

export function* cancelMultibrandPolicySaga(dispatch, { payload }) {
  const { policyId } = payload;
  const url = `policy/MultiBrand/Policy/Cancel/${policyId}`;

  try {
    const response = yield call(post, dispatch, url);

    if (response.success) {
      yield put(cancelMultibrandPolicySuccess());
    } else {
      yield put(cancelMultibrandPolicyError());
    }
  } catch (e) {
    yield put(cancelMultibrandPolicyError());
  }
}

export function* getHasCareCreditUrl(dispatch) {
  const url = 'customer/Policy/HasCareCreditUrl';
  // pending V2

  try {
    const response = yield call(get, dispatch, url);

    if (response && response.success) {
      yield put(getHasCareCreditUrlSuccess(response));
    } else {
      yield put(getHasCareCreditUrlFailed());
    }
  } catch {
    yield put(getHasCareCreditUrlFailed());
  }
}

export function* requestCancelRenewal(dispatch, { payload }) {
  const url = 'purchase/StopAutoRenew';

  try {
    const {
      customerName,
      effectiveDate,
      effectiveDateUtc,
      marketingChannel,
      nowTimestampUtc,
      petName,
      policyNumber,
      systemName,
    } = payload;

    const params = {
      cancellationEffectiveDate: effectiveDate,
      cancellationEffectiveDateUtc: effectiveDateUtc,
      cancellationRequestedTimestamp: nowTimestampUtc,
      cancellationRequestedTimestampUtc: nowTimestampUtc,
      customerName,
      marketingChannel,
      petNames: [petName],
      policyNumber,
      policyTermId: '',
      systemName,
    };

    const response = yield call(post, dispatch, url, params);

    if (response && response.success) {
      yield put(requestCancelRenewalSuccess(response));
    } else {
      yield put(requestCancelRenewalFailed());
    }
  } catch {
    yield put(requestCancelRenewalFailed());
  }
}

// Orca Canada change coverage
export function* getProxyPolicy({ payload }) {
  try {
    const { managementUrl, policyId } = payload;
    const token = yield call(getData, WEBVIEW_API_TOKEN);

    const origin =
      process.env.REACT_APP_API_URL.replace('/dev', '').replace('/tst', '');
    const path = `${origin}/orca-proxy/policy/v1/${policyId}`;
    const url =
      `${path}?base-url=${managementUrl}&localizationCode=${i18next.language}`;
    const params = {
      authorization: `Bearer ${token}`,
      headers: SUBSCRIPTION_KEY_HEADER,
      method: 'GET',
      url,
    };

    const request = yield call(doFetch, params);

    if (request.ok) {
      const response = yield request.json();

      yield put(getProxyPolicySuccess(response));
    } else {
      yield put(getProxyPolicyFailed());
    }
  } catch (_) {
    yield put(getProxyPolicyFailed());
  }
}

export function* getOrcaCoveragePreview({ payload }) {
  try {
    const {
      accidentIllnessProduct,
      accidentOnlyProduct,
      paymentManagementUrl,
      petId,
    } = payload;

    const managementUrl = paymentManagementUrl;

    const path = `quote/v2/preview/${petId}`;
    const url = `${managementUrl}/${path}?localizationCode=${i18next.language}`;

    const accidentIllnessParams = {
      body: {
        ...accidentIllnessProduct,
        petId,
      },
      method: 'POST',
      url,
    };
    const accidentOnlyParams = {
      body: {
        ...accidentOnlyProduct,
        petId,
      },
      method: 'POST',
      url,
    };

    const accidentIllnessRequest = yield call(doFetch, accidentIllnessParams);
    const accidentOnlyRequest = yield call(doFetch, accidentOnlyParams);

    if (accidentIllnessRequest.ok
      && accidentOnlyRequest.ok) {
      const accidentIllnessResponse = yield accidentIllnessRequest.json();
      const accidentOnlyResponse = yield accidentOnlyRequest.json();

      yield put(getOrcaCoveragePreviewSuccess({
        monthlyPreviewResults: accidentIllnessResponse.monthlyPreviewResults,
        selectedProductCode: accidentIllnessProduct.selectedProductCode,
        stateFilingProductId: accidentIllnessProduct.stateFilingProductId,
      }));
      yield put(getOrcaCoveragePreviewSuccess({
        monthlyPreviewResults: accidentOnlyResponse.monthlyPreviewResults,
        selectedProductCode: accidentOnlyProduct.selectedProductCode,
        stateFilingProductId: accidentOnlyProduct.stateFilingProductId,
      }));
    } else {
      yield put(getOrcaCoveragePreviewFailed());
    }
  } catch (_) {
    yield put(getOrcaCoveragePreviewFailed());
  }
}

const getPreview = async ({
  coinsuranceId = 0,
  coverageLimitId = 0,
  deductibleId = 0,
  incidentLimitId = 0,
  managementUrl,
  petId,
  selectedProductCode = '',
  stateFilingProductId = 0,
}) => {
  let data = null;

  try {
    const path = `quote/v2/preview/${petId}`;
    const url = `${managementUrl}/${path}?localizationCode=${i18next.language}`;
    const params = {
      body: {
        coinsuranceId,
        coverageLimitId,
        deductibleId,
        incidentLimitId,
        petId,
        selectedProductCode,
        stateFilingProductId,
      },
      method: 'POST',
      url,
    };

    const request = await doFetch(params);

    if (request.ok) {
      const response = await request.json();

      data = response;
    }
  } catch (_) {
    doNothing();
  }

  return data;
};

export function* getOrcaCaQuoteProducts({ payload }) {
  try {
    const { managementUrl, pet, petId, productName } = payload;

    const path = `quote/v2/quote-pet/${petId}/products`;
    const url = `${managementUrl}/${path}?localizationCode=${i18next.language}`;
    const params = { method: 'GET', url };

    const request = yield call(doFetch, params);

    if (request.ok) {
      const response = yield request.json();

      const productCode = getOrcaPolicyCodeByName(productName);
      const productSelected = response.baseProducts
        .find(({ code }) => code === productCode);
      const { selectedLimits } = pet.coverage;
      const deductibleLimit = productSelected.limits.deductibleLimits
        .find(({ id }) => id === selectedLimits.deductibleId);
      const incidentLimit = productSelected.limits.incidentLimits
        .find(({ id }) => id === selectedLimits.incidentLimitId);
      const coinsuranceLimit = productSelected.limits.coinsuranceLimits
        .find(({ id }) => id === selectedLimits.coinsuranceId);
      const coverageLimit = productSelected.limits.coverageLimits
        .find(({ id }) => id === selectedLimits.coverageLimitId);

      const body = {
        ...selectedLimits,
        managementUrl,
        petId: pet.petId,
        selectedProductCode: productSelected.code,
        stateFilingProductId: productSelected.stateFilingProductId,
      };

      const previewData = yield call(getPreview, body);

      yield put(getCoverageQuoteProductSuccess({
        ...response,
        previewData: {
          coinsuranceLimit,
          coverageLimit,
          deductibleLimit,
          incidentLimit,
          monthlyPreviewResults: previewData.monthlyPreviewResults,
          productSelected: {
            code: productSelected.code,
            display: productSelected.display,
            stateFilingProductId: productSelected.stateFilingProductId,
          },
        },
      }));
    } else {
      yield put(getCoveragePeriodFailure());
    }
  } catch (_) {
    yield put(getCoveragePeriodFailure());
  }
}

function* getWellnessDetail({ managementUrl, marketChannelId, postalCode }) {
  let wellnessOptionsDetails = [];

  try {
    const state = getOrcaStateByPostalCode(postalCode);
    const petId = 2;
    const query = { localizationCode: i18next.language, marketChannelId };
    const path =
      `quote/v1/state/${state}/species/${petId}/wellness-benefit-schedule`
      + `?${serializeForUri(query)}`;
    const url = `${managementUrl}/${path}`;
    const params = { method: 'GET', url };

    const request = yield call(doFetch, params);

    if (request.ok) {
      wellnessOptionsDetails = yield request.json();
    }
  } catch (_) {
    doNothing();
  }

  return wellnessOptionsDetails;
}

export function* getCoveragePeriod({ payload }) {
  try {
    const {
      coveragePeriodId,
      currentPetId,
      effectiveOn,
      managementUrl,
      productName,
    } = payload;

    const path = `quote/v1/coverage-period/${coveragePeriodId}`;
    const body = {
      continuingCoverage: false,
      currentPetId,
      effectiveOn,
      partnerCode: null,
      targetCoveragePeriodId: null,
    };

    const url = `${managementUrl}/${path}`;

    const params = { body, method: 'POST', url };

    const request = yield call(doFetch, params);

    if (request.ok) {
      const response = yield request.json();

      // WIP: pet
      const { customer, pets, marketChannelId, postalCode } = response;
      const pet = pets[0];
      const data = {
        coverage: pet?.coverage || {},
        customer,
        effectiveOn: response.effectiveOn,
        expiresOn: response.expiresOn,
        marketChannelId,
        petId: pet?.petId,
        petName: pet?.petName,
        pets,
        quoteGuid: response.quoteGuid,
        stateFilingId: response.stateFilingId,
      };

      const wellnessOptionsDetails = yield call(getWellnessDetail, {
        managementUrl, marketChannelId, postalCode,
      });

      yield call(getOrcaCaQuoteProducts, {
        payload: {
          ...payload,
          pet,
          petId: pet?.petId,
          productName,
        },
      });
      yield put(getCoveragePeriodSuccess({ ...data, wellnessOptionsDetails }));
    } else {
      yield put(getCoveragePeriodFailure());
    }
  } catch (_) {
    yield put(getCoveragePeriodFailure());
  }
}

export function* saveOrcaQuote({ payload }) {
  try {
    const store = yield select(({
      billingPayments,
      policies,
    }) => ({ billingPayments, policies }));

    const {
      billingPayments: { updatePaymentMethodCa },
      policies: { changeCoverageOrcaCanada },
    } = store;
    const {
      customer,
      orcaMarketChannelId,
      petId,
      pets,
      proxyPolicy,
      quoteGuid,
      stateFilingId,
    } = changeCoverageOrcaCanada;
    const {
      customerId,
      paymentManagementUrl,
    } = updatePaymentMethodCa;

    const { postalCode } = customer;
    const [policyTerm] = proxyPolicy.policyTerms;

    const {
      internalPetId,
      selectedLimits,
      selectedProducts,
    } = payload;

    const pet = pets[0];

    const path = `quote/v1?policyTermId=${policyTerm.policyTermId}`;
    const body = {
      currentPetId: petId,
      customer: {
        ...customer,
        customerId,
      },
      internalPetId,
      marketChannelId: orcaMarketChannelId,
      pets: [{
        ...pet,
        coverage: {
          selectedLimits: {
            coinsuranceId: selectedLimits.coinsuranceId,
            coverageLimitId: selectedLimits.coverageLimitId,
            deductibleId: selectedLimits.deductibleId,
            incidentLimitId: selectedLimits.incidentLimitId,
          },
          selectedProducts,
        },
        petId,
      }],
      postalCode,
      quoteGuid,
      stateFilingId,
    };

    const url = `${paymentManagementUrl}/${path}`;

    const token = yield call(getData, WEBVIEW_API_TOKEN);
    const authorization = `Bearer ${token}`;
    const params = { authorization, body, method: 'POST', url };
    const request = yield call(doFetch, params);

    if (request.ok) {
      const response = yield request.json();

      yield put(saveOrcaQuoteSuccess(response.data));
    } else {
      yield put(saveOrcaQuoteFailed());
    }
  } catch (_) {
    yield put(saveOrcaQuoteFailed());
  }
}

export function* submitCoveragePeriod({ payload }) {
  try {
    const token = yield call(getData, WEBVIEW_API_TOKEN);
    const {
      coveragePeriodId,
      customerId,
      managementUrl,
      quoteGuid,
    } = payload;

    const origin =
      process.env.REACT_APP_API_URL.replace('/dev', '').replace('/tst', '');
    const path = 'orca-proxy/policy/v1/coverage-period/'
      + `${coveragePeriodId}/quote/${quoteGuid}/${customerId}`
      + `?localizationCode=${i18next.language}`
      + `&base-url=${managementUrl}`;

    const url = `${origin}/${path}`;

    const params = {
      authorization: `Bearer ${token}`,
      headers: SUBSCRIPTION_KEY_HEADER,
      method: 'POST',
      url,
    };

    const request = yield call(doFetch, params);

    if (request.ok) {
      const response = yield request.json();
      yield put(submitCoveragePeriodSuccess(response));
    } else {
      yield put(submitCoveragePeriodSuccess());
    }
  } catch (_) {
    yield put(submitCoveragePeriodFailed());
  }
}
// Orca Canada change coverage

export function* policiesSaga(store) {
  yield all([
    takeLatest(GET_MULTIBRAND_POLICIES, getMultibrandPolicies, store.dispatch),
    takeLatest(
      GET_MULTIBRAND_DEFAULT_POLICY,
      getMultibrandDefaultPolicy,
      store.dispatch,
    ),
    takeLatest(GET_POLICY_DETAIL, getPolicyDetail, store.dispatch),

    // ORCA POLICIES
    takeLatest(GET_ORCA_POWER_UP, getOrcaPowerUp, store.dispatch),
    takeEvery(GET_ORCA_POWER_UPS_BATCH, getOrcaPowerUpsBatch, store.dispatch),
    takeLatest(
      VALIDATE_BREED_MULTIBRAND,
      validateBreedMultibrand,
      store.dispatch,
    ),
    takeLatest(
      CANCEL_MULTIBRAND_POLICY,
      cancelMultibrandPolicySaga,
      store.dispatch,
    ),
    takeLatest(GET_COVERAGE_PERIOD_REQUEST, getCoveragePeriod),
    takeLatest(GET_ORCA_COVERAGE_PREVIEW, getOrcaCoveragePreview),
    takeLatest(SAVE_ORCA_QUOTE, saveOrcaQuote),
    takeLatest(GET_PROXY_POLICY, getProxyPolicy),
    takeLatest(SUBMIT_COVERAGE_PERIOD, submitCoveragePeriod),
  ]);
}
